import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Search, X } from "lucide-react";
import { api, formatPrice, API } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const STATUS_FILTERS = ["all", "pending", "confirmed", "packed", "shipped", "delivered", "cancelled"];

async function downloadCsv(path, filename) {
    const token = localStorage.getItem("ost_token") || "";
    const res = await fetch(`${API}${path}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

export default function AdminOrders() {
    const { settings } = useSettings();
    const cur = settings?.currency_symbol || "Rs.";
    const [orders, setOrders] = useState([]);
    const [filter, setFilter] = useState("all");
    const [exporting, setExporting] = useState(false);
    const [search, setSearch] = useState("");
    const [debounced, setDebounced] = useState("");
    const [loading, setLoading] = useState(false);

    // Debounce search input -> server query
    useEffect(() => {
        const id = setTimeout(() => setDebounced(search.trim()), 300);
        return () => clearTimeout(id);
    }, [search]);

    useEffect(() => {
        setLoading(true);
        const params = debounced ? { search: debounced } : {};
        api.get("/admin/orders", { params })
            .then((r) => setOrders(r.data || []))
            .finally(() => setLoading(false));
    }, [debounced]);

    const list = filter === "all" ? orders : orders.filter((o) => o.status === filter);

    const exportCsv = async () => {
        setExporting(true);
        try {
            await downloadCsv("/admin/exports/orders.csv", `opensea-orders-${new Date().toISOString().slice(0,10)}.csv`);
        } catch (_) {}
        setExporting(false);
    };

    const isSearching = debounced.length > 0;

    return (
        <div className="space-y-5" data-testid="admin-orders-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-semibold">Operations</div>
                    <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Orders</h1>
                </div>
                <button
                    onClick={exportCsv}
                    disabled={exporting}
                    className="inline-flex items-center gap-2 border border-[#27272A] hover:border-[#00BCE5] hover:text-[#00BCE5] text-white px-4 py-2.5 rounded-sm font-display font-semibold uppercase tracking-widest text-sm disabled:opacity-50"
                    data-testid="orders-export-csv"
                >
                    <Download size={14} /> {exporting ? "Exporting…" : "Export CSV"}
                </button>
            </div>

            <div className="relative max-w-xl">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/45" />
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by order number, customer name, or phone…"
                    className="w-full bg-[#0A0A0B] border border-[#27272A] rounded-sm py-2.5 pl-9 pr-9 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#00BCE5]"
                    data-testid="orders-search-input"
                />
                {search && (
                    <button
                        onClick={() => setSearch("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-white/45 hover:text-white p-1"
                        aria-label="Clear search"
                        data-testid="orders-search-clear"
                    >
                        <X size={14} />
                    </button>
                )}
            </div>

            <div className="flex flex-wrap gap-2">
                {STATUS_FILTERS.map((s) => (
                    <button
                        key={s}
                        onClick={() => setFilter(s)}
                        className={`px-3.5 py-1.5 rounded-sm text-xs font-display uppercase tracking-widest border ${
                            filter === s ? "border-[#00BCE5] text-[#00BCE5] bg-[#00BCE5]/10" : "border-[#27272A] text-white/65"
                        }`}
                        data-testid={`orders-filter-${s}`}
                    >
                        {s}
                    </button>
                ))}
            </div>

            <div className="bg-[#18181B] border border-[#27272A] rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[#121214] text-xs uppercase tracking-widest font-display text-white/55">
                        <tr>
                            <th className="px-3 py-2 text-left">Order #</th>
                            <th className="px-3 py-2 text-left">Customer</th>
                            <th className="px-3 py-2 text-left">Phone</th>
                            <th className="px-3 py-2 text-left">Items</th>
                            <th className="px-3 py-2 text-left">Total</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-left">Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((o) => (
                            <tr key={o.id} className="border-t border-[#27272A]" data-testid={`order-row-${o.order_number}`}>
                                <td className="px-3 py-2">
                                    <Link to={`/admin/orders/${o.id}`} className="text-[#00BCE5] hover:underline font-medium">{o.order_number}</Link>
                                </td>
                                <td className="px-3 py-2">{o.shipping_address?.full_name}</td>
                                <td className="px-3 py-2 text-white/65">{o.shipping_address?.phone}</td>
                                <td className="px-3 py-2 text-white/85">{(o.items || []).reduce((s, i) => s + i.quantity, 0)}</td>
                                <td className="px-3 py-2">{formatPrice(o.total, cur)}</td>
                                <td className="px-3 py-2 text-xs uppercase tracking-widest font-display text-[#00BCE5]">{o.status}</td>
                                <td className="px-3 py-2 text-white/55">{new Date(o.created_at).toLocaleString()}</td>
                            </tr>
                        ))}
                        {list.length === 0 && !loading && (
                            <tr>
                                <td colSpan={7} className="px-3 py-10 text-center text-white/55" data-testid="orders-empty-state">
                                    {isSearching ? (
                                        <>
                                            <div className="font-display uppercase tracking-widest text-white/75 mb-1">No matches</div>
                                            <div className="text-xs">
                                                We couldn't find any orders matching "<span className="text-white">{debounced}</span>".
                                                Try a different order number, name, or phone.
                                            </div>
                                        </>
                                    ) : (
                                        <>No orders {filter !== "all" ? `with status "${filter}"` : "yet"}.</>
                                    )}
                                </td>
                            </tr>
                        )}
                        {loading && list.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-3 py-8 text-center text-white/45" data-testid="orders-loading">
                                    Loading orders…
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
